import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "@/lib/useStore";
import { X, ArrowRight } from "lucide-react";

const STORAGE_KEY = "vw_sticky_cta_dismissed_v1";

/**
 * Apple-TV-style sticky bottom signup bar.
 * Auto-shows once user scrolls past `showAfterPx`.
 * Dismissable, stored in localStorage with a 7-day TTL.
 */
export default function StickyBottomCTA({
  tenantName,
  tenantSlug,
  accentColor = "#F59E0B",
  themeColor = "#7C3AED",
  cheapestPriceLabel = "",
  showAfterPx = 600,
}) {
  const navigate = useNavigate();
  const { sp } = useStore();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.tenant === tenantSlug && parsed.until > Date.now()) {
          setDismissed(true);
        }
      }
    } catch (e) {
      // ignore — never block render on storage errors
    }
  }, [tenantSlug]);

  useEffect(() => {
    if (dismissed) return;
    const onScroll = () => setVisible(window.scrollY > showAfterPx);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [dismissed, showAfterPx]);

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ tenant: tenantSlug, until: Date.now() + 7 * 24 * 60 * 60 * 1000 }),
      );
    } catch (e) { /* ignore */ }
  };

  if (dismissed || !visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 border-t backdrop-blur-md"
      style={{ backgroundColor: `${accentColor}f0`, borderColor: `${accentColor}` }}
      data-testid="sticky-bottom-cta"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-3 md:py-4 flex items-center gap-3 md:gap-6">
        <button
          type="button"
          onClick={dismiss}
          className="text-black/70 hover:text-black grid place-items-center w-7 h-7 -ml-1"
          aria-label="Dismiss banner"
          data-testid="sticky-bottom-cta-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.35em] text-black/65 font-medium hidden md:block">
            Subscribe to {tenantName}
          </div>
          <div className="font-display font-bold text-sm md:text-base text-black truncate">
            Stream {tenantName} unlimited{cheapestPriceLabel ? ` · ${cheapestPriceLabel}` : ""}.
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate(sp(`/signup`))}
          className="bg-black text-white px-4 md:px-6 py-2.5 md:py-3 text-[11px] md:text-xs uppercase tracking-[0.25em] font-bold inline-flex items-center gap-1.5 hover:bg-black/85 transition-colors flex-shrink-0"
          style={{ borderColor: themeColor }}
          data-testid="sticky-bottom-cta-button"
        >
          Start Watching <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
